import { DepositResult } from '../../../api/web3/deposit-api'
import { Token } from '../../../logic/token/token'
import { Wei } from '../../../utils/types'
import { TokenWithData } from '../../../api/api-types'
import { Pool } from '../../../logic/pools/Pool'
import { Hash } from '../../transactions/TransactionContext'

export interface ISubstate {
  error?: boolean
  message?: string
  accepted?: number
  deposit?: DepositResult
  allowed?: Token[]
  accepting?: Token[]
  userShareExit?: Wei
  amounts?: Map<TokenWithData, Wei>
  pool?: Pool
  transactionHash?: Hash
}

export type SubState = ISubstate | undefined

export interface ViewState {
  key: string
  next: (context?: any) => ViewState
  subState: SubState
  isAfter(target: this): boolean
  isBefore(target: this): boolean
  isIn(targets: this[]): boolean
  // It's not a problem to have a substate. It's up to the reducer to work with
  // the substate mechanics, not to the 'true' app state
  withSubState(subState: SubState): this
  getMessage(context?: any): string
}

export const defaultStateSelector: StateSelector = state => {
  /*no-op*/
}
export type StateSelector = (state: ViewState, newSubstate?: any) => void

// TODO: Reuse the one from SwapState
export abstract class AbstractViewState implements ViewState {
  public subState: SubState = undefined
  public message: string = ''

  clear() {
    this.subState = undefined
    return this
  }
  withSubState(subState: SubState) {
    this.subState = subState
    return this
  }

  withError(errorMessage: string) {
    this.subState = {
      error: true,
      message: errorMessage,
    }
    return this
  }

  withMessage(message: string) {
    this.message = message
    return this
  }

  abstract isAfter(target: this): boolean
  abstract isBefore(target: this): boolean
  abstract isIn(targets: this[]): boolean
  abstract key: string

  abstract next(context: any): ViewState

  getMessage(context?: any): string {
    if (this.subState && this.subState.error && this.subState.message) {
      return this.subState.message
    }
    return this.message
  }
}

export class DefaultViewState extends AbstractViewState {
  subState: any = undefined

  constructor(public key: string, subState?: any) {
    super()
    if (subState) {
      this.subState = subState
    }
  }

  public states: ViewState[] = []

  isAfter(target: this): boolean {
    const c = this.states.findIndex(s => s.key === this.key)
    const t = this.states.findIndex(s => s.key === target.key)
    return c >= t
  }
  isBefore(target: this): boolean {
    const c = this.states.findIndex(s => s.key === this.key)
    const t = this.states.findIndex(s => s.key === target.key)
    return c <= t
  }
  isIn(targets: this[]): boolean {
    return targets.some(t => t === this)
  }

  next(context?: any): ViewState {
    throw 'not implemented in Default State'
  }
}

class DepositState extends DefaultViewState {
  // TODO: there is a min here !!!! use subState
  constructor(public key: string, public subState: SubState = undefined) {
    super(key, subState)
  }

  next(context?: any): ViewState {
    switch (this.key) {
      case DepositStates.CONNECT.key:
        return DepositStates.CHOOSE_AMOUNT
      case DepositStates.CHOOSE_AMOUNT.key:
        return DepositStates.VALID
      case DepositStates.VALID.key:
        return DepositStates.PREVIEW
      case DepositStates.PREVIEW.key:
        return DepositStates.ALLOWANCE_LOADING
      case DepositStates.ALLOWANCE_LOADING.key:
        return DepositStates.PROCESSING
      case DepositStates.PROCESSING.key:
        return DepositStates.TRANSACTION_SUCCESS
    }
    return DepositStates.CHOOSE_AMOUNT
  }
}

// Written here for auto-completion
export const DepositStates = {
  CONNECT: new DepositState('CONNECT'),
  CHOOSE_AMOUNT: new DepositState('CHOOSE_AMOUNT'),
  VALID: new DepositState('VALID'),
  PREVIEW: new DepositState('PREVIEW'),
  ALLOWANCE_LOADING: new DepositState('ALLOWANCE_LOADING', {
    allowed: [],
    accepting: [],
  }),
  ALLOWANCE_DONE: new DepositState('ALLOWANCE_DONE'),
  ALLOWANCE_FAILED: new DepositState('ALLOWANCE_FAILED'),
  PROCESSING: new DepositState('PROCESSING'),
  TRANSACTION_SUCCESS: new DepositState('TRANSACTION_SUCCESS'),
  TRANSACTION_FAILED: new DepositState('TRANSACTION_FAILED'),
}

const allStates = Object.values(DepositStates)
allStates.forEach(state => (state.states = allStates))
